import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getOfferCalculationsAsync, deleteOfferCalculationAsync } from "../../../../slices/offer-calculation";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { PlusIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { updateUrlWithException } from "../../../../components/Utils";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { CalculationsTable } from "../../../../components/dashboard/offers/calculations/calculations-table";
import CalculationVersionHistoryDialog from "../../../../components/dashboard/offers/calculations/calculation-version-history";
import CalculationCreateDialog from "../../../../components/dashboard/offers/calculations/calculation-create-dialog";
import ProConfirmation from "../../../../components/utils/ProConfirmation";
import { isOfferCalculationTemplateVersionsEnabled } from "../../../../components/dashboard/offers/utils";
import toast from "react-hot-toast";

export const OfferCalculationsList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const [controller, setController] = useState({
    filters: [],
    page: searchParams.get("page") ? parseInt(searchParams.get("page")) : 1,
    per_page: searchParams.get("per_page") ? parseInt(searchParams.get("per_page")) : 10,
    query: searchParams.get("query") || "",
    sort: searchParams.get("sort") || "DESC",
    sortBy: searchParams.get("sortBy") || "created_at",
    active: searchParams.get("active") || null,
    calculation_type: searchParams.get("calculation_type") || null,
  });

  const itemState = useSelector((store) => {
    return store.offerCalculations;
  });
  const offerFeatureFlags = useSelector(
    (store) => store.offers?.offerFeatureFlags || {}
  );
  const versionsEnabled = isOfferCalculationTemplateVersionsEnabled(offerFeatureFlags);

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [calculationToDelete, setCalculationToDelete] = useState(null);
  const [versionHistoryCalculation, setVersionHistoryCalculation] = useState(null);

  // Update URL params
  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        active: controller.active,
        calculation_type: controller.calculation_type,
      },
      "filters"
    );
  }, [controller]);

  const { isLoading, error, errors, data, totalCount, currentCount } = itemState;

  const handleQueryChange = (newQuery) => {
    setController({
      ...controller,
      page: 1,
      query: newQuery,
    });
  };

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleFiltersChange = (filters) => {
    // Convert ModuleFilter format to backend format
    const backendFilters = [];

    // Handle calculation_type filter
    if (filters.calculation_types && filters.calculation_types.length > 0) {
      backendFilters.push({
        name: "calculation_type",
        value: filters.calculation_types[0],
      });
    }

    // Handle active filter (inactive_calculations checkbox)
    if (filters.inactive_calculations) {
      backendFilters.push({
        name: "active",
        value: "false",
      });
    } else {
      backendFilters.push({
        name: "active",
        value: "true",
      });
    }

    // Extract active and calculation_type from filters
    const activeFilter = filters.inactive_calculations ? "false" : "true";
    const calculationTypeFilter = filters.calculation_types && filters.calculation_types.length > 0
      ? filters.calculation_types[0]
      : null;

    setController({
      ...controller,
      page: 1,
      query: filters.query || controller.query,
      filters: backendFilters,
      active: activeFilter,
      calculation_type: calculationTypeFilter,
    });
  };

  const debouncedDispatchAction = debounce((controller) => {
    // Convert controller to API format
    const apiParams = {
      page: controller.page,
      per_page: controller.per_page,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
      active: controller.active,
      calculation_type: controller.calculation_type,
    };
    dispatch(getOfferCalculationsAsync(apiParams));
  }, 250);

  useEffect(() => {
    debouncedDispatchAction(controller);

    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const handleDelete = (calculation, e) => {
    e.stopPropagation();
    setCalculationToDelete(calculation);
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = async () => {
    if (calculationToDelete) {
      try {
        await dispatch(deleteOfferCalculationAsync(calculationToDelete.id)).unwrap();
        toast.success(t("offer.calculation.delete_success"));
        setDeleteConfirmationOpen(false);
        setCalculationToDelete(null);
        // Refresh the list
        const apiParams = {
          page: controller.page,
          per_page: controller.per_page,
          query: controller.query,
          sort: controller.sort,
          sortBy: controller.sortBy,
          active: controller.active,
          calculation_type: controller.calculation_type,
        };
        dispatch(getOfferCalculationsAsync(apiParams));
      } catch (error) {
        toast.error(t("offer.calculation.delete_error"));
      }
    }
  };

  // Custom checkbox button filters for calculations
  const customCheckboxButtonFilters = [
    {
      key: "inactive_calculations",
      label: t("Inaktive kalkyler"),
      icon: ExclamationTriangleIcon,
      defaultValue: false,
    }
  ];

  const calculationTypeOptions = [
    { value: "standard", label: t("offer.calculation.type.standard") },
    { value: "product", label: t("offer.calculation.type.product") },
    { value: "hours", label: t("offer.calculation.type.hours") },
    { value: "car_allowance", label: t("offer.calculation.type.car_allowance") },
    { value: "nested", label: t("offer.calculation.type.nested") },
  ];

  // Convert backend filters to ModuleFilter format
  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query,
      calculation_types: controller.calculation_type ? [controller.calculation_type] : [],
      inactive_calculations: controller.active === "false",
    };

    return initialFilters;
  };

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={[]}
        title={t("offer.calculation.title")}
        mainButton={{
          label: t("offer.calculation.new"),
          action: () => setCreateDialogOpen(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={getInitialFilters()}
              searchPlaceholder={t("offer.calculation.search_placeholder")}
              filterTitle={t("Filtrer kalkyler")}
              customCheckboxButtonFilters={customCheckboxButtonFilters}
              customDropdownFilters={[
                {
                  key: "calculation_types",
                  label: t("offer.calculation.type"),
                  options: calculationTypeOptions,
                  icon: null,
                },
              ]}
            />

            <CalculationsTable
              itemState={itemState}
              itemsCount={totalCount}
              currentCount={currentCount}
              page={controller.page}
              perPage={controller.per_page}
              onPageChange={handlePageChange}
              onDelete={handleDelete}
              versionsEnabled={versionsEnabled}
              onViewVersions={setVersionHistoryCalculation}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Delete Confirmation Dialog */}
      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={t("offer.calculation.delete_confirm")}
        action="delete"
        module="offer_calculation"
        yesCallback={confirmDelete}
        cancelCallback={() => {
          setDeleteConfirmationOpen(false);
          setCalculationToDelete(null);
        }}
      />

      <CalculationVersionHistoryDialog
        open={Boolean(versionHistoryCalculation)}
        onClose={() => setVersionHistoryCalculation(null)}
        calculationId={versionHistoryCalculation?.id}
        onRestored={() => {
          const apiParams = {
            page: controller.page,
            per_page: controller.per_page,
            query: controller.query,
            sort: controller.sort,
            sortBy: controller.sortBy,
            active: controller.active,
            calculation_type: controller.calculation_type,
          };
          dispatch(getOfferCalculationsAsync(apiParams));
        }}
      />

      <CalculationCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={() => {
          const apiParams = {
            page: controller.page,
            per_page: controller.per_page,
            query: controller.query,
            sort: controller.sort,
            sortBy: controller.sortBy,
            active: controller.active,
            calculation_type: controller.calculation_type,
          };
          dispatch(getOfferCalculationsAsync(apiParams));
        }}
      />
    </>
  );
};


